/**
 * 审核待办提示音（纯函数 + WebAudio 双音提示，无音频资源文件依赖）
 *
 * 触发链路：`hooks/useReviewBadges.ts` 60s 轮询四条审核链（入金/提现/代理商开户/商户开户），
 * 待办总数较上次快照「增加」时播放提示音。规则：
 * - 仅在前后两次快照【全部链路计数非 null】时比较（null = 加载失败/未加载，总数不可信，
 *   避免链路恢复时把回升误报为新增）；
 * - 仅「增加」触发，减少/不变不响。
 *
 * 浏览器自动播放策略：用户未与页面交互前 AudioContext 处于 suspended，resume 失败即静默跳过，
 * 不报错不阻塞（降级为无提示音，角标仍正常刷新）。
 *
 * 纯函数部分可被 node 直跑单测：node --experimental-strip-types src/lib/reviewSound.test.ts
 */

/** 四条审核链待办快照：null = 该链路未加载/加载失败（不可信值） */
export type ReviewBadgeCounts = {
  depositPendingCount: number | null;
  withdrawalReviewableCount: number | null;
  agentOnboardingPendingCount: number | null;
  merchantOnboardingPendingCount: number | null;
};

/** 快照求和：任一链路为 null → 整体不可信，返回 null */
export function sumReviewCounts(counts: ReviewBadgeCounts): number | null {
  const { depositPendingCount, withdrawalReviewableCount, agentOnboardingPendingCount, merchantOnboardingPendingCount } = counts;
  if (
    depositPendingCount == null ||
    withdrawalReviewableCount == null ||
    agentOnboardingPendingCount == null ||
    merchantOnboardingPendingCount == null
  ) {
    return null;
  }
  return (
    depositPendingCount + withdrawalReviewableCount + agentOnboardingPendingCount + merchantOnboardingPendingCount
  );
}

/** 仅「可信总数增加」返回 true（首次快照 / 任一链路不可信 / 未增加均不响） */
export function shouldAlertReviewIncrease(prevTotal: number | null, nextTotal: number | null): boolean {
  if (prevTotal == null || nextTotal == null) {
    return false;
  }
  return nextTotal > prevTotal;
}

let audioCtx: AudioContext | null = null;

/** 双音提示音（WebAudio 合成，无资源文件）；任何失败静默降级 */
export function playReviewSound(): void {
  try {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) {
      return;
    }
    audioCtx ??= new Ctor();
    if (audioCtx.state === 'suspended') {
      void audioCtx.resume().catch(() => undefined);
    }
    const t0 = audioCtx.currentTime;
    // A5 → E6 两声「叮咚」，短促不刺耳
    const tones: Array<{ freq: number; at: number }> = [
      { freq: 880, at: 0 },
      { freq: 1318.51, at: 0.16 },
    ];
    for (const { freq, at } of tones) {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, t0 + at);
      gain.gain.exponentialRampToValueAtTime(0.18, t0 + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + at + 0.28);
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.start(t0 + at);
      osc.stop(t0 + at + 0.32);
    }
  } catch {
    // 音频不可用（无设备/策略限制）→ 静默降级，不影响角标
  }
}
